import React, { useEffect, useRef } from "react";
import { checkIsPerformer } from "shared-auth";
import { isIOS } from "shared-utils";
import { useLocalStorage } from "usehooks-ts";
import { add, isAfter } from "date-fns";
import { useTranslation } from "i18n";
import { SpotlightRoundelOnDarkBg as RoundedLogo } from "assets";
import Portal from "../Portal";
import {
  appStoreFallbackTimeDelta,
  dismissedTimestampStoreKey,
  SMART_BANNER_CONTAINER_ID,
} from "./constants";
import { config } from "../../../config";

export interface Props {
  appPath: string;
}

const PerformerAppSmartBanner = ({ appPath = "" }: Props) => {
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>();
  const { t } = useTranslation();
  const [dismissedTimestamp, setDismissedTimestamp] = useLocalStorage(
    dismissedTimestampStoreKey,
    ""
  );

  const isHidden = dismissedTimestamp
    ? isAfter(new Date(dismissedTimestamp), new Date())
    : false;
  const isPerformer = checkIsPerformer();
  const appId = config?.slPerformerApp?.appStoreId;

  const handleVisibilityChange = () => {
    if (document.hidden && timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  useEffect(
    () => () =>
      document.removeEventListener("visibilitychange", handleVisibilityChange),
    []
  );

  if (!isPerformer || !isIOS() || isHidden) return null;

  const open = () => {
    const appLink = `spotlight://${appPath}`;
    const appStoreLink = `https://apps.apple.com/app/id${appId}`;

    document.addEventListener("visibilitychange", handleVisibilityChange);

    window.location.href = appLink;

    timeoutRef.current = setTimeout(() => {
      window.location.href = appStoreLink;
    }, appStoreFallbackTimeDelta);
  };

  const dismiss = () => {
    const dismissUntil = add(new Date(), { days: 30 });
    setDismissedTimestamp(dismissUntil.toISOString());
  };

  return (
    <Portal querySelector={`#${SMART_BANNER_CONTAINER_ID}`}>
      <div className="top-0 left-0 right-0 mx-auto flex items-center gap-3 w-full z-[9999] px-3 py-2 bg-grey-snow-0 font-sans text-color-neutral-one">
        <button
          className="flex-shrink-0 w-6 h-6 flex items-center justify-center text-gray-500 hover:text-gray-700"
          onClick={dismiss}
        >
          &times;
        </button>
        <div className="w-12 h-12 flex items-center justify-center rounded-lg overflow-hidden flex-shrink-0 bg-black">
          <RoundedLogo width={40} height={40} />
        </div>
        <div className="flex flex-col flex-1 min-w-0">
          <div className="font-semibold text-[15px] leading-tight truncate">
            {t("common:smartBanner.title")}
          </div>
          <div className="text-sm text-gray-500">
            {t("common:smartBanner.slogan")}
          </div>
        </div>
        <button
          onClick={open}
          className="flex-shrink-0 inline-flex items-center justify-center px-3 py-1.5 font-semibold text-sm text-white rounded-full no-underline bg-color-brand-one"
        >
          {t("common:button.label.open")}
        </button>
      </div>
    </Portal>
  );
};

export default PerformerAppSmartBanner;
